import React from 'react';
import { styled } from '@linaria/react';

import { range, normalize, clamp } from '@/utils';
import useMousePositionWithinElement from '@/hooks/use-mouse-position-within-element';

import {
  convertToDecimal,
  convertToHexadecimal,
} from './HexColorPicker.helpers';

const NUM_OF_STOPS = 10;

function Slider({
  value,
  channel,
  color,
  dynamicGradients,
  onChange,
}) {
  const wrapperRef = React.useRef();

  const id = React.useId();

  const [mousePosition, boundingBox] = useMousePositionWithinElement(
    wrapperRef,
    { buffer: 128 }
  );
  const [isMouseDown, setIsMouseDown] = React.useState(false);

  React.useEffect(() => {
    if (!isMouseDown || !mousePosition || !boundingBox) {
      return;
    }

    let newValue = (mousePosition.y / boundingBox.height) * 255;
    newValue = 255 - Math.round(clamp(newValue, 0, 255));

    onChange(channel, newValue);
  }, [isMouseDown, mousePosition, boundingBox, channel, onChange]);

  React.useEffect(() => {
    function handleMouseUp() {
      setIsMouseDown(false);
    }

    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('touchend', handleMouseUp);
    document.addEventListener('mouseleave', handleMouseUp);

    return () => {
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchend', handleMouseUp);
      document.removeEventListener('mouseleave', handleMouseUp);
    };
  }, [isMouseDown]);

  const decimalValue = convertToDecimal(value);
  const handlePercentage = normalize(decimalValue, 0, 255, 100, 0);

  function handleKeyDown(ev) {
    switch (ev.key) {
      case 'ArrowUp': {
        ev.preventDefault();
        onChange(channel, clamp(decimalValue + 10, 0, 255));
        break;
      }
      case 'ArrowDown': {
        ev.preventDefault();
        onChange(channel, clamp(decimalValue - 10, 0, 255));
        break;
      }
    }
  }

  const stops = React.useMemo(() => {
    return range(NUM_OF_STOPS).map((index) => {
      const stopColorObj = dynamicGradients
        ? { ...color }
        : { r: '00', g: '00', b: '00' };

      stopColorObj[channel] = convertToHexadecimal(
        Math.round(normalize(index, 0, NUM_OF_STOPS - 1, 0, 255))
      );

      return formatColor(stopColorObj);
    });
  }, [channel, color, dynamicGradients]);

  return (
    <Wrapper>
      {value}
      <SliderButton
        ref={wrapperRef}
        id={`slider-${id}`}
        style={{ background: formatGradient(stops) }}
        onMouseDown={() => setIsMouseDown(true)}
        onTouchStart={() => setIsMouseDown(true)}
        onKeyDown={handleKeyDown}
      >
        <HandleWrapper>
          <Handle
            style={{
              top: `${handlePercentage}%`,
            }}
          />
        </HandleWrapper>
      </SliderButton>
    </Wrapper>
  );
}

const formatColor = ({ r, g, b }) => `#${r}${g}${b}`;

const formatGradient = (stops) => {
  return `linear-gradient(0deg, ${stops.join(', ')})`;
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-right: 8px;
`;

const SliderButton = styled.button`
  position: relative;
  display: block;
  width: 40px;
  height: 200px;
  margin-top: 8px;
  border-radius: 4px;
  border: 2px solid white;
  box-shadow:
    0.6px 1.3px 2.2px rgba(0, 0, 0, 0.02),
    1.5px 3.1px 5.3px rgba(0, 0, 0, 0.028),
    2.9px 5.8px 10px rgba(0, 0, 0, 0.035),
    5.1px 10.3px 17.9px rgba(0, 0, 0, 0.042),
    9.6px 19.2px 33.4px rgba(0, 0, 0, 0.05),
    23px 46px 80px rgba(0, 0, 0, 0.07);
  user-select: none;
  touch-action: none;
  -webkit-tap-highlight-color: transparent;

  &:focus {
    outline: none;
  }
`;

const HandleWrapper = styled.span`
  position: absolute;
  top: 0;
  left: 0;
  height: calc(100% - 16px);
  width: 100%;
`;

const Handle = styled.span`
  position: absolute;
  left: -4px;
  display: block;
  height: 16px;
  width: calc(100% + 8px);
  border: 2px solid black;
  box-shadow: inset 0px 0px 0px 2px white;
  border-radius: 1px;
  cursor: grab;

  ${SliderButton}:active & {
    cursor: grabbing;
  }

  ${SliderButton}:focus-visible & {
    outline-offset: 8px;
    outline: 2px auto;
    outline-color: Highlight;
    outline-color: -webkit-focus-ring-color;
  }
`;

export default React.memo(Slider);
